import React, { useState, useRef, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ExclamationTriangleIcon, 
  CubeTransparentIcon,
  CursorArrowRaysIcon,
  PhotoIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { PlayCircleIcon } from '@heroicons/react/24/solid'; // Solid icon for the play button
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BarChart, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

// === CONFIGURATION ===
const BUCKET_NAME = 'public-assets';
// Assuming REACT_APP_SUPABASE_URL is defined in your .env
const BASE_MEDIA_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/${BUCKET_NAME}`;

// === DATA CONFIGURATION ===
const FEATURE_DATA = [
  {
    id: 'visualization',
    label: 'GIS Visualization',
    features: [
      {
        id: '3d-twin',
        title: '3D IRI Chart',
        description: 'Visualize IRI profiles directly above the physical road surface.  Identify roughness patterns in three dimensions for a more intuitive understanding of road conditions.',
        media: 'MAP_KML.mp4', 
        type: 'video', 
        icon: CubeTransparentIcon,
      },
      {
        id: 'seg-analysis',
        title: 'Segment Analysis',
        description: 'Analyze the run segment by segment with customizable color coding. Quickly identify trouble spots and visualize how different segments compare against each other.  Additionally use the synced KML & Elevation profiles for deeper insights.',
        media: 'MAP_ANALYSIS_SEG.mp4',
        type: 'video',
        icon: ChartBarIcon,
      },
      {
        id: 'alr-analysis',
        title: 'ALR Analysis',
        description: 'Spot Areas of Localized Roughness (ALRs) on the map and analyze their starting and ending stations. View the density of ALRs along the route to prioritize maintenance planning effectively.',
        media: 'MAP_ALR.mp4',
        type: 'video',
        icon: ExclamationTriangleIcon,
      },
      {
        id: 'photo-replay',
        title: 'Photo Playback',
        description: 'Replay your data collection run with synchronized site photos. Watch the map marker track your position precisely as images cycle automatically at up to 8x speed.',
        media: 'MAP_PHOTO.mp4', 
        type: 'video',
        icon: PhotoIcon
      },
      {
        id: 'chart-sync',
        title: 'Synchronized Charting',
        description: 'Scrub through your data on the linear chart and watch the map instantly jump to that station. Investigate the exact location of ALRs, bumps, and dips.',
        media: 'MAP_CHART.mp4',
        type: 'video',
        icon: LineChart
      }
    ]
  },
  {
    id: 'analysis',
    label: 'Deep Analysis',
    features: [
      {
        id: 'fixed-interval',
        title: 'Fixed Interval',
        description: 'View all segments in a bar chart view against the project threshold.  Quickly identify segments that exceed the threshold and require attention.',
        media: 'ANALYSIS_FIXED.mp4',
        type: 'video',
        icon: BarChart
      },
      {
        id: 'IRI & Elevation Charts',
        title: 'IRI & Elevation Charts',
        description: 'Analyze the IRI and Elevation profiles in detail with interactive charts. Zoom in on specific areas, hover for exact values, and correlate elevation changes with roughness patterns for deeper insights.',
        media: 'ANALYSIS_CHART.mp4',
        type: 'video',
        icon: CursorArrowRaysIcon,
      },
      {
        id: 'alr-summary',
        title: 'ALR Summary',
        description: 'View all Areas of Localized Roughness (ALRs) detected in the run.  Analyze their severity, length, and exact stationing to prioritize maintenance efforts effectively. Filter by segment, view in the map, and export for reporting.',
        media: 'ANALYSIS_ALR.mp4',
        type: 'video',
        icon: ExclamationTriangleIcon
      }
    ]
  }
];

const FeatureMedia = ({ feature, url }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const onPlay = () => setIsPlaying(true);
  const onPause = () => setIsPlaying(false);

  return (
    <div className="w-full mb-8 px-0 lg:px-12">
      <div 
        className="w-full max-w-5xl mx-auto bg-slate-900 lg:rounded-lg shadow-2xl overflow-hidden border-y lg:border border-slate-800 aspect-[16/10] relative group cursor-pointer"
        onClick={feature.type === 'video' && !isPlaying ? handlePlayClick : undefined}
      >
        {/* Subtle Grid Pattern Background */}
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none"></div>

        {feature.type === 'video' ? (
          <>
            <video
              ref={videoRef}
              // 1. APPEND #t=0.001 TO FORCE RENDER ON IOS
              src={`${url}#t=0.001`} 
              // 2. PRELOAD METADATA TO FETCH FIRST FRAME
              preload="metadata" 
              // 3. ADD bg-black TO HIDE DOTTED PATTERN WHILE LOADING
              className="w-full h-full object-cover z-10 relative bg-black" 
              playsInline
              controls={isPlaying}
              onPlay={onPlay}
              onPause={onPause}
            />
            
            {!isPlaying && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 group-hover:bg-black/30 transition-all">
                <div className="transform group-hover:scale-110 transition-transform duration-200">
                   <PlayCircleIcon className="w-20 h-20 text-white drop-shadow-xl opacity-90" />
                </div>
              </div>
            )}
          </>
        ) : (
          <img 
            src={url} 
            alt={feature.title}
            className="w-full h-full object-contain z-10 relative"
          />
        )}
      </div>
    </div>
  );
};

// === MAIN COMPONENT ===
export default function FeatureHighlight() {
  const [activeFeatureId, setActiveFeatureId] = useState(FEATURE_DATA[0].features[0].id);
  const sectionRefs = useRef({});

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80; 
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveFeatureId(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-grow flex flex-col lg:flex-row max-w-[1440px] mx-auto w-full pt-16 font-sans text-slate-900">
        
        {/* SIDEBAR */}
        <aside className="hidden lg:block w-72 border-r border-slate-200 flex-shrink-0 bg-white/50 backdrop-blur-sm h-[calc(100vh-64px)] sticky top-16 overflow-y-auto">
          <div className="p-8">
            <h2 className="text-sm font-semibold text-slate-900 mb-6 uppercase tracking-wider">
              Platform Features
            </h2>
            <nav className="space-y-8">
              {FEATURE_DATA.map((category) => (
                <div key={category.id}>
                  <h3 className="text-sm font-medium text-slate-900 mb-3 px-2">
                    {category.label}
                  </h3>
                  <ul className="space-y-1">
                    {category.features.map((feature) => {
                      const isActive = activeFeatureId === feature.id;
                      return (
                        <li key={feature.id}>
                          <button
                            onClick={() => scrollToSection(feature.id)}
                            className={`group flex items-center text-left w-full px-2 py-1.5 text-sm rounded-md transition-all duration-200
                              ${isActive 
                                ? 'text-red-600 bg-red-50 font-medium' 
                                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                              }
                            `}
                          >
                            {isActive && (
                              <span className="absolute left-0 w-1 h-6 bg-red-600 rounded-r-full"></span>
                            )}
                            <span className="truncate">{feature.title}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <section className="flex-1 flex flex-col min-w-0 bg-white">
            {FEATURE_DATA.map((category, catIndex) => (
              <div key={category.id}>
                {category.features.map((feature, featIndex) => {
                  const currentMediaUrl = `${BASE_MEDIA_URL}/${feature.media}`;
                  const isLastItem = catIndex === FEATURE_DATA.length - 1 && featIndex === category.features.length - 1;

                  return (
                    <div 
                      key={feature.id}
                      id={feature.id}
                      ref={(el) => (sectionRefs.current[feature.id] = el)}
                      className={`flex flex-col ${!isLastItem ? 'border-b border-slate-100' : ''}`}
                    >
                      {/* Text Header */}
                      <div className="px-6 lg:px-12 pt-16 pb-8 max-w-5xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-red-50 rounded-lg">
                            <feature.icon className="w-6 h-6 text-red-600" />
                          </div>
                          <span className="text-sm font-semibold text-red-600 tracking-wide uppercase">
                            {category.label}
                          </span>
                        </div>
                        
                        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
                          {feature.title}
                        </h1>
                        
                        <p className="text-lg text-slate-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>

                      {/* Video/Image Component */}
                      <FeatureMedia feature={feature} url={currentMediaUrl} />

                      {/* Tips */}
                      <div className="px-6 lg:px-12 max-w-5xl pb-16">
                        {feature.tip && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 mb-8">
                            <div className="flex-shrink-0 mt-0.5">
                              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <h3 className="text-sm font-medium text-amber-900">Pro Tip</h3>
                              <div className="mt-1 text-sm text-amber-700">
                                {feature.tip}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            <section className="py-12 bg-red-600 scroll-mt-20">
  <div className="max-w-6xl mx-auto px-8">
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
      
      {/* Left Column - Content */}
      <div className="text-white text-left">
        <h2 className="text-3xl font-bold mb-3">
          Get Started with Ames Connect
        </h2>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">
            Why schedule onboarding?
          </h3>
          <p className="text-red-50 text-sm">
            Our team will walk you through setup, answer your questions, and ensure you're configured for success from day one.
          </p>
        </div>

        {/* Software Notice */}
        <div className="bg-red-700 border-l-4 border-white p-4 mb-6">
          <p className="text-white text-sm font-semibold mb-1">
            Updated Desktop Software Required
          </p>
          <p className="text-red-100 text-sm mb-2">
            Install the latest version of Ames Pro Software before using the platform.
          </p>
          <Link 
            to="/download"
            className="text-white font-semibold text-sm inline-flex items-center hover:underline"
          >
            Download Software
            <ArrowRightIcon className="ml-1 h-4 w-4" />
          </Link>
        </div>

        {/* Support */}
        <p className="text-sm text-red-100">
          Questions?{' '}
          <a href="mailto:sales@amesengineering.com" className="text-white hover:underline font-semibold">
            sales@amesengineering.com
          </a>
          {' • '}
          <a href="tel:+15152928194" className="text-white hover:underline font-semibold">
            515-292-8194
          </a>
        </p>
      </div>

      {/* Right Column - Action Card */}
      <div className="bg-white border border-gray-200 shadow-lg p-6 text-left">
        
        {/* Primary Action */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-gray-900">
              Schedule Onboarding
            </h3>
            <span className="bg-red-600 text-white px-2 py-1 text-xs font-semibold uppercase">
              Recommended
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Get personalized setup and training from our team
          </p>
          
          <a 
            href="https://www.amesengineering.com/sales/salesRequest" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-full inline-flex items-center justify-center bg-red-600 text-white px-6 py-3 font-semibold hover:bg-red-700 transition-colors"
          >
            Schedule Now
            <ArrowTopRightOnSquareIcon className="ml-2 h-5 w-5" />
          </a>
        </div>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Secondary Action */}
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            Start on Your Own
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Create an account and explore self-guided resources
          </p>
          
          <Link 
            to="/signup"
            className="w-full inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 px-6 py-2.5 font-semibold hover:bg-gray-50 transition-colors"
          >
            Create Account
          </Link>
        </div>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-600 pt-4 border-t border-gray-100">
          Already have an account?{' '}
          <Link to="/login" className="text-red-600 hover:text-red-700 font-semibold">
            Log in
          </Link>
        </p>
      </div>

    </div>
  </div>
</section>
        </section>
      </main>
      
      <div className="lg:hidden">
        <Footer />
      </div>
    </div>
  );
}